<template>
  <div class="tt-wrap">
    <div class="tt-topbar">
      <span class="tt-label">⭕ Tic Tac Toe</span>
      <span class="tt-wins">W: <b>{{ wins }}</b></span>
      <span class="tt-losses">L: <b>{{ losses }}</b></span>
      <span class="tt-draws">D: <b>{{ draws }}</b></span>
      <span class="tt-score">Score: <b>{{ score }}</b></span>
    </div>

    <div class="tt-content">
      <div class="tt-status" :class="statusClass">{{ statusMsg }}</div>

      <div class="tt-board">
        <div
          v-for="(cell, i) in board"
          :key="i"
          class="tt-cell"
          :class="{ 'tt-x': cell === 'X', 'tt-o': cell === 'O', 'tt-win-cell': winLine.includes(i) }"
          @click="makeMove(i)"
        >
          <transition name="tt-pop">
            <span v-if="cell" :key="cell">{{ cell }}</span>
          </transition>
        </div>
      </div>

      <div class="tt-actions">
        <button class="tt-btn" @click="restart">↺ New Game</button>
        <div class="tt-turn-info">
          <span v-if="!gameOver && myTurn" class="tt-your-turn">Your turn (X)</span>
          <span v-else-if="!gameOver && !myTurn" class="tt-ai-turn">VIKI thinking...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const WIN_LINES = [
  [0,1,2],[3,4,5],[6,7,8],
  [0,3,6],[1,4,7],[2,5,8],
  [0,4,8],[2,4,6],
];

export default {
  name: 'TicTacToe1',
  data() {
    return {
      board: Array(9).fill(''),
      myTurn: true,
      gameOver: false,
      winLine: [],
      wins: 0, losses: 0, draws: 0,
      score: 0,
    };
  },
  computed: {
    statusMsg() {
      if (this.gameOver) {
        if (this.winLine.length) {
          return this.board[this.winLine[0]] === 'X' ? '🎉 You win!' : '😞 VIKI wins!';
        }
        return "🤝 It's a draw!";
      }
      return this.myTurn ? 'Your turn' : 'VIKI thinking...';
    },
    statusClass() {
      if (!this.gameOver) return 'tt-status-neutral';
      if (this.winLine.length) {
        return this.board[this.winLine[0]] === 'X' ? 'tt-status-win' : 'tt-status-lose';
      }
      return 'tt-status-draw';
    },
  },
  methods: {
    makeMove(i) {
      if (!this.myTurn || this.gameOver || this.board[i]) return;
      this.$set(this.board, i, 'X');
      if (this.checkWin('X')) { this.endGame('X'); return; }
      if (this.board.every(c => c)) { this.endGame(null); return; }
      this.myTurn = false;
      setTimeout(() => this.aiMove(), 350);
    },
    aiMove() {
      const best = this.minimax(this.board, 'O');
      this.$set(this.board, best.idx, 'O');
      if (this.checkWin('O')) { this.endGame('O'); return; }
      if (this.board.every(c => c)) { this.endGame(null); return; }
      this.myTurn = true;
    },
    minimax(board, player, depth = 0) {
      const opp = player === 'O' ? 'X' : 'O';
      const w = WIN_LINES.find(l => l.every(i => board[i] === player));
      if (w) return { score: player === 'O' ? 10 - depth : depth - 10 };
      const wOpp = WIN_LINES.find(l => l.every(i => board[i] === opp));
      if (wOpp) return { score: opp === 'O' ? 10 - depth : depth - 10 };
      const empty = board.map((c,i) => c ? -1 : i).filter(i => i !== -1);
      if (!empty.length) return { score: 0 };
      const moves = empty.map(idx => {
        const b2 = [...board]; b2[idx] = player;
        return { idx, score: this.minimax(b2, opp, depth + 1).score };
      });
      if (player === 'O') return moves.reduce((a, b) => b.score > a.score ? b : a);
      return moves.reduce((a, b) => b.score < a.score ? b : a);
    },
    checkWin(p) {
      const line = WIN_LINES.find(l => l.every(i => this.board[i] === p));
      if (line) { this.winLine = line; return true; }
      return false;
    },
    endGame(winner) {
      this.gameOver = true;
      if (winner === 'X') { this.wins++; this.score += 10; }
      else if (winner === 'O') { this.losses++; this.score = Math.max(0, this.score - 5); }
      else { this.draws++; this.score += 2; }
      this.$emit('score-update', this.score);
      this.$emit('game-over', this.score);
    },
    restart() {
      this.board = Array(9).fill('');
      this.myTurn = true;
      this.gameOver = false;
      this.winLine = [];
    },
  },
};
</script>

<style scoped>
.tt-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  min-height: 360px;
}
.tt-topbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: #f8f9ff;
  border-bottom: 1px solid #e8e8f0;
  font-size: 13px;
  color: #333;
  flex-shrink: 0;
}
.tt-label { font-weight: 700; color: #7c5cfc; }
.tt-wins b { color: #22c55e; }
.tt-losses b { color: #ef4444; }
.tt-draws b { color: #f59e0b; }
.tt-score { margin-left: auto; font-weight: 700; color: #7c5cfc; }

.tt-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  gap: 16px;
}

.tt-status {
  font-size: .95rem;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 20px;
  min-width: 160px;
  text-align: center;
}
.tt-status-neutral { background: #f1f5f9; color: #475569; }
.tt-status-win { background: #dcfce7; color: #16a34a; }
.tt-status-lose { background: #fee2e2; color: #dc2626; }
.tt-status-draw { background: #fef9c3; color: #ca8a04; }
.tt-your-turn { color: #7c5cfc; }
.tt-ai-turn { color: #f59e0b; }

.tt-board {
  display: grid;
  grid-template-columns: repeat(3, 100px);
  grid-template-rows: repeat(3, 100px);
  gap: 8px;
}
.tt-cell {
  background: #f8f9ff;
  border: 2px solid #e2e4f0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-weight: 900;
  cursor: pointer;
  transition: background .12s, border-color .12s, transform .1s;
  user-select: none;
}
.tt-cell:hover:not(.tt-x):not(.tt-o) {
  background: #ede9fe;
  border-color: #7c5cfc;
  transform: scale(1.04);
}
.tt-cell.tt-x { color: #7c5cfc; border-color: #7c5cfc; background: #f5f3ff; cursor: default; }
.tt-cell.tt-o { color: #ef4444; border-color: #ef4444; background: #fff5f5; cursor: default; }
.tt-cell.tt-win-cell { background: #fef9c3 !important; border-color: #f59e0b !important; animation: tt-pulse .5s ease; }

@keyframes tt-pulse {
  0%,100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}
.tt-pop-enter-active { animation: tt-pop-in .2s ease; }
@keyframes tt-pop-in {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.tt-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}
.tt-btn {
  background: #7c5cfc;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 20px;
  font-size: .88rem;
  font-weight: 700;
  cursor: pointer;
}
.tt-btn:hover { opacity: .88; }
.tt-turn-info { font-size: .82rem; min-width: 120px; }

@media (max-width: 480px) {
  .tt-board {
    grid-template-columns: repeat(3, 80px);
    grid-template-rows: repeat(3, 80px);
  }
  .tt-cell { font-size: 2rem; border-radius: 10px; }
}
</style>
